import { createContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react'
import type { AuthState, AuthUser } from './useAuth'

export const AuthContext = createContext<AuthState | null>(null)

const TOKEN_KEY = 'droid_token'
const STATE_KEY = 'droid_oauth_state'

// OIDC configuration for Authentik
const OIDC_CONFIG = {
  authorizeUrl: import.meta.env.VITE_OIDC_AUTHORIZE_URL ?? 'https://sso.hayani.xyz/application/o/authorize/',
  clientId: import.meta.env.VITE_OIDC_CLIENT_ID ?? 'Unp7MXo9HAp13SszqtLdqbFpW4o9tTz5Lu4sJsIG',
  redirectUri: import.meta.env.VITE_OIDC_REDIRECT_URI ?? `${window.location.origin}/`,
  tokenProxyUrl: '/api/auth/token',
  scopes: 'openid profile email offline_access',
}

/** Decode a JWT payload without verification (verification happens on the gateway) */
function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const base64 = token.split('.')[1]
    if (!base64) return null
    const json = atob(base64.replace(/-/g, '+').replace(/_/g, '/'))
    return JSON.parse(json)
  } catch {
    return null
  }
}

function extractUser(payload: Record<string, unknown>): AuthUser {
  return {
    sub: (payload.sub as string) ?? '',
    email: (payload.email as string) ?? '',
    name: (payload.name as string) ?? '',
    groups: (payload.groups as string[]) ?? [],
  }
}

/** Check if a JWT expires within the given margin (seconds) */
function isTokenExpiringSoon(token: string, marginSec = 60): boolean {
  const payload = decodeJwtPayload(token)
  if (!payload?.exp) return true
  const expMs = (payload.exp as number) * 1000
  return Date.now() > expMs - marginSec * 1000
}

/** Generate a random state parameter for CSRF protection */
function generateState(): string {
  const array = new Uint8Array(32)
  crypto.getRandomValues(array)
  return Array.from(array, (b) => b.toString(16).padStart(2, '0')).join('')
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => {
    const stored = localStorage.getItem(TOKEN_KEY)
    if (stored && !isTokenExpiringSoon(stored, 0)) return stored
    if (stored) localStorage.removeItem(TOKEN_KEY)
    return null
  })
  const [user, setUser] = useState<AuthUser | null>(() => {
    if (!token) return null
    const payload = decodeJwtPayload(token)
    return payload ? extractUser(payload) : null
  })
  const [isLoading, setIsLoading] = useState(false)
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Schedule token refresh before expiry
  const scheduleRefresh = useCallback((jwt: string) => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current)
    const payload = decodeJwtPayload(jwt)
    if (!payload?.exp) return

    const expMs = (payload.exp as number) * 1000
    const refreshAt = expMs - Date.now() - 60_000 // refresh 60s before expiry
    if (refreshAt <= 0) return

    refreshTimer.current = setTimeout(async () => {
      try {
        const res = await fetch(OIDC_CONFIG.tokenProxyUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: new URLSearchParams({
            grant_type: 'refresh_token',
            refresh_token: localStorage.getItem('droid_refresh_token') ?? '',
          }),
        })
        if (res.ok) {
          const data = await res.json()
          const newToken = data.access_token as string
          localStorage.setItem(TOKEN_KEY, newToken)
          if (data.refresh_token) {
            localStorage.setItem('droid_refresh_token', data.refresh_token)
          }
          setToken(newToken)
          const newPayload = decodeJwtPayload(newToken)
          if (newPayload) setUser(extractUser(newPayload))
          scheduleRefresh(newToken)
        }
      } catch {
        // Refresh failed — user will need to re-authenticate on next API call
      }
    }, refreshAt)
  }, [])

  // On mount, schedule refresh if we have a valid token
  useEffect(() => {
    if (token) scheduleRefresh(token)
    return () => {
      if (refreshTimer.current) clearTimeout(refreshTimer.current)
    }
  }, [token, scheduleRefresh])

  const login = useCallback(() => {
    const state = generateState()
    sessionStorage.setItem(STATE_KEY, state)
    const params = new URLSearchParams({
      response_type: 'code',
      client_id: OIDC_CONFIG.clientId,
      redirect_uri: OIDC_CONFIG.redirectUri,
      scope: OIDC_CONFIG.scopes,
      state,
    })
    window.location.href = `${OIDC_CONFIG.authorizeUrl}?${params}`
  }, [])

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem('droid_refresh_token')
    sessionStorage.removeItem(STATE_KEY)
    if (refreshTimer.current) clearTimeout(refreshTimer.current)
    setToken(null)
    setUser(null)
  }, [])

  const handleCallback = useCallback(async () => {
    const params = new URLSearchParams(window.location.search)
    const code = params.get('code')
    const returnedState = params.get('state')
    const savedState = sessionStorage.getItem(STATE_KEY)

    if (!code) return
    if (returnedState && savedState && returnedState !== savedState) {
      throw new Error('OAuth state mismatch')
    }

    setIsLoading(true)
    try {
      const res = await fetch(OIDC_CONFIG.tokenProxyUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          grant_type: 'authorization_code',
          redirect_uri: OIDC_CONFIG.redirectUri,
          code,
        }),
      })

      if (!res.ok) {
        throw new Error(`Token exchange failed: ${res.status}`)
      }

      const data = await res.json()
      const accessToken = data.access_token as string
      localStorage.setItem(TOKEN_KEY, accessToken)
      if (data.refresh_token) {
        localStorage.setItem('droid_refresh_token', data.refresh_token)
      }
      sessionStorage.removeItem(STATE_KEY)

      setToken(accessToken)
      const payload = decodeJwtPayload(accessToken)
      if (payload) setUser(extractUser(payload))
      scheduleRefresh(accessToken)

      // Clean the URL of auth params
      window.history.replaceState({}, '', window.location.pathname)
    } finally {
      setIsLoading(false)
    }
  }, [scheduleRefresh])

  const value: AuthState = {
    isAuthenticated: token !== null,
    user,
    token,
    isLoading,
    login,
    logout,
    handleCallback,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
